import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Code, ArrowRight, CheckCircle } from 'lucide-react';
import { ServiceType } from '../types';
import { useSeo } from '../hooks/useSeo';

interface HomeProps {
  setContext: (ctx: ServiceType) => void;
}

const LOGOS = [
  { name: 'COLDWELL BANKER', className: 'font-serif font-bold text-xl md:text-2xl text-white' },
  { name: 'KBC INVESTMENTS', className: 'font-sans font-bold text-xl md:text-2xl text-white tracking-tighter' },
  { name: 'TECHFLOW', className: 'font-mono font-bold text-lg md:text-xl text-white' },
  { name: 'RUDRAM', className: 'font-sans font-black text-xl md:text-2xl text-white tracking-widest' },
  { name: 'RYDEV', className: 'font-mono font-bold text-lg md:text-xl text-white italic' },
];

const Home: React.FC<HomeProps> = ({ setContext }) => {
  
  useEffect(() => {
    setContext(ServiceType.NONE);
  }, [setContext]);

  // SEO Implementation
  useSeo({
    title: "Pinaka | Strategic Digital Defense & Creation",
    description: "Pinaka serves the intersection of IT security and high-performance web development. We fortify your infrastructure and architect your digital presence.",
    keywords: ["IT Security Kelowna", "Web Development Agency", "Managed Services", "WebGL", "Cybersecurity", "React Developers"],
    schema: {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Pinaka",
        "url": "https://pinaka.ca",
        "logo": "https://pinaka.ca/logo.png",
        "description": "Strategic Digital Defense & Creation. Merging Managed IT Security with High-End Web Architecture.",
        "address": {
            "@type": "PostalAddress",
            "addressLocality": "Kelowna",
            "addressRegion": "BC",
            "addressCountry": "CA"
        },
        "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+1-250-555-0199",
            "contactType": "customer service",
            "email": "info@pinaka.ca"
        }
    }
  });

  return (
    <div className="relative w-full min-h-[calc(100vh-80px)] flex flex-col">
      
      {/* Main Content Container */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 md:px-12 py-12">
        
        {/* Unifying Header - Wrapped in Glass for readability */}
        <div className="text-center mb-16 max-w-3xl animate-in fade-in slide-in-from-bottom-4 duration-700 bg-black/60 backdrop-blur-md p-8 rounded-3xl border border-white/5 shadow-2xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
                Strategic <span className="text-pinaka-silver">Defense</span> & <br/>
                Digital <span className="text-white">Creation</span>
            </h1>
            <p className="text-white text-lg font-light">
                Pinaka serves the intersection of security and scale. We fortify your infrastructure and architect your digital presence.
            </p>
        </div>

        {/* Split Pathways */}
        <div className="grid md:grid-cols-2 gap-6 w-full max-w-5xl">
            
            {/* Security Path */}
            <div className="group relative"
                onMouseEnter={() => setContext(ServiceType.SECURITY)}
                onMouseLeave={() => setContext(ServiceType.NONE)}
            >
                <Link to="/security" className="block h-full bg-pinaka-panel border border-pinaka-border hover:border-pinaka-cyan p-8 md:p-10 rounded-xl transition-all duration-300 hover:shadow-2xl hover:shadow-pinaka-cyan/10 hover:-translate-y-1">
                    <div className="w-12 h-12 bg-pinaka-cyan/10 rounded-lg flex items-center justify-center mb-6 text-pinaka-cyan">
                        <Shield className="w-6 h-6" strokeWidth={1.5} />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2 group-hover:text-pinaka-cyan transition-colors">IT Security & Managed Services</h2>
                    <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                        For organizations that cannot afford downtime. Enterprise-grade compliance, Blue Team defense, and 24/7 monitoring.
                    </p>
                    <ul className="space-y-2 mb-8">
                        <li className="flex items-center gap-2 text-xs text-gray-300 font-mono"><CheckCircle className="w-3 h-3 text-pinaka-cyan"/> SOC2 Compliance Ready</li>
                        <li className="flex items-center gap-2 text-xs text-gray-300 font-mono"><CheckCircle className="w-3 h-3 text-pinaka-cyan"/> Microsoft Partner</li>
                        <li className="flex items-center gap-2 text-xs text-gray-300 font-mono"><CheckCircle className="w-3 h-3 text-pinaka-cyan"/> Ransomware Protection</li>
                    </ul>
                    <div className="flex items-center gap-2 text-xs font-bold text-white uppercase tracking-widest mt-auto">
                        Explore Defense <ArrowRight className="w-4 h-4 text-pinaka-cyan" />
                    </div>
                </Link>
            </div>

            {/* Web Dev Path */}
            <div className="group relative"
                onMouseEnter={() => setContext(ServiceType.WEB_DEV)}
                onMouseLeave={() => setContext(ServiceType.NONE)}
            >
                <Link to="/web-dev" className="block h-full bg-pinaka-panel border border-pinaka-border hover:border-pinaka-violet p-8 md:p-10 rounded-xl transition-all duration-300 hover:shadow-2xl hover:shadow-pinaka-violet/10 hover:-translate-y-1">
                     <div className="w-12 h-12 bg-pinaka-violet/10 rounded-lg flex items-center justify-center mb-6 text-pinaka-violet">
                        <Code className="w-6 h-6" strokeWidth={1.5} />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2 group-hover:text-pinaka-violet transition-colors">Web Architecture & 3D</h2>
                    <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                        For brands that demand impact. High-performance React applications, immersive WebGL experiences, and scalable digital products.
                    </p>
                    <ul className="space-y-2 mb-8">
                        <li className="flex items-center gap-2 text-xs text-gray-300 font-mono"><CheckCircle className="w-3 h-3 text-pinaka-violet"/> Next.js Performance</li>
                        <li className="flex items-center gap-2 text-xs text-gray-300 font-mono"><CheckCircle className="w-3 h-3 text-pinaka-violet"/> 3D Interactive Models</li>
                        <li className="flex items-center gap-2 text-xs text-gray-300 font-mono"><CheckCircle className="w-3 h-3 text-pinaka-violet"/> Headless CMS Scale</li>
                    </ul>
                    <div className="flex items-center gap-2 text-xs font-bold text-white uppercase tracking-widest mt-auto">
                        Explore Creation <ArrowRight className="w-4 h-4 text-pinaka-violet" />
                    </div>
                </Link>
            </div>

        </div>
      </div>

      {/* Trust Bar Static */}
      <div className="w-full border-t border-white/5 bg-black/40 backdrop-blur-sm py-10 pb-10">
          <div className="max-w-7xl mx-auto px-6 flex flex-col items-center justify-center gap-8">
             <div className="text-xs font-mono text-gray-400 uppercase tracking-widest text-center">Trusted by Industry Leaders</div>
             
             {/* Centered Static Logos */}
             <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60 grayscale hover:grayscale-0 transition-all duration-500 hover:opacity-100">
                {LOGOS.map((logo, i) => (
                   <span key={`logo-${i}`} className={logo.className}>{logo.name}</span>
                ))}
             </div>

          </div>
      </div>

    </div>
  );
};

export default Home;